import React, { useState } from 'react';
import { useSEO } from '../lib/useSEO';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Send, Mail, ArrowRight, CheckCircle } from 'lucide-react';
import api from '../lib/api';
import { useAuthStore } from '../lib/auth';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

export default function Contact() {
  useSEO('Contact Us | JepPrep', 'Get in touch with the JepPrep team for questions, feedback, or partnership inquiries.', { canonical: 'https://jepprep.com/contact' });
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const { user } = useAuthStore();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    setSending(true);
    try {
      await api.post('/api/contact', { name: name.trim(), email: email.trim(), message: message.trim() });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="contact-page">
      {/* Header */}
      <header className="border-b border-[hsl(var(--border))]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="contact-logo">
            <img src={LOGO_URL} alt="JepPrep" className="h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-3">
            {user ? (
              <Link to="/dashboard">
                <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-gold-400))] font-semibold" data-testid="contact-dashboard-btn">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-gray-400 hover:text-white">Sign In</Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-gold-400))] font-semibold">
                    Start Free
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-16">
        {sent ? (
          <div className="text-center py-12" data-testid="contact-success">
            <div className="w-16 h-16 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">Message sent!</h1>
            <p className="text-gray-400 mb-8">Thank you for reaching out. We'll get back to you as soon as possible.</p>
            <Link to="/">
              <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-gold-400))] font-semibold">
                Back to Home <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="text-center mb-10">
              <div className="w-12 h-12 rounded-xl bg-[hsl(var(--brand-gold-500))]/10 flex items-center justify-center mx-auto mb-5">
                <Mail className="w-6 h-6 text-[hsl(var(--brand-gold-500))]" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3" data-testid="contact-heading">Get in touch</h1>
              <p className="text-gray-400">Have a question, suggestion, or just want to say hi? We'd love to hear from you.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Name</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] text-white py-5"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Email</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] text-white py-5"
                  data-testid="contact-email"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Message</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="How can we help?"
                  rows={6}
                  className="w-full bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] rounded-lg p-3 text-white placeholder:text-gray-600 resize-y text-sm leading-relaxed focus:outline-none focus:border-[hsl(var(--brand-gold-500))]/50"
                  data-testid="contact-message"
                />
              </div>
              <Button
                type="submit"
                disabled={sending}
                className="w-full py-5 text-base bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-gold-400))] font-bold"
                data-testid="contact-submit"
              >
                <Send className="w-4 h-4 mr-2" /> {sending ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-[hsl(var(--border))] py-8 mt-auto">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-gray-600">&copy; {new Date().getFullYear()} JepPrep. All rights reserved.</p>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
